import type { ReactElement } from "react";
import { ICON_SIZES } from "odyssey-shared";

/**
 * An icon the kit draws: a Pixelarticons glyph from the free 24 set under `src/ui/art/icons/`.
 */
export type IconName = "menu" | "close" | "alert";

/**
 * An icon role (ADR-061, amendment "the design system lands"): where an icon sits decides its size, which
 * the role's token gives, so P1.9-T20 moves every icon of a role to its purchased set by changing the token.
 */
export type IconRole = keyof typeof ICON_SIZES;

/**
 * The generated class that draws an icon at a size by `mask-position`, tinted with `currentColor`.
 *
 * @param name The icon.
 * @param size Its size in logical pixels, the native size of its set.
 * @returns `ot-ui-icon-<name>_<size>`.
 */
export function iconClass(name: IconName, size: number): string {
  return `ot-ui-icon-${name}_${size}`;
}

/**
 * An icon in the current text colour, at its role's size, never resampled. Without a label it is
 * decoration and hidden from assistive technology; with one it is an image of that name.
 *
 * @param props The icon, its role, and a label when the icon alone carries meaning.
 * @returns The icon.
 */
export function Icon({ name, role, label }: { readonly name: IconName; readonly role: IconRole; readonly label?: string }): ReactElement {
  const size = ICON_SIZES[role];
  if (label === undefined) {
    return <span className={`ot-icon ${iconClass(name, size)}`} data-kit="icon" aria-hidden="true" />;
  }
  return <span className={`ot-icon ${iconClass(name, size)}`} data-kit="icon" role="img" aria-label={label} />;
}
